<template>
  <div class="card-header border-bottom pt-1 pb-1">
    <h4 class="card-title">Salary Structure</h4>
  </div>

  <div class="card-body mt-2">
    <div class="row">
      <div class="col-12 col-md-4">
        <div v-if="salaryTypeData.salary_type_name" class="mb-1">
          <strong>Salary Type:</strong> {{ salaryTypeData.salary_type_name }}
        </div>

        <div v-if="salaryTypeData.scale_amount && salaryTypeData.grade_number" class="mb-1">
          <strong>Salary Grade Scale:</strong>
          {{ salaryTypeData.grade_number }} ({{ salaryTypeData.scale_amount }})
        </div>

        <table class="table table-bordered table-sm mt-3">
          <thead class="table-light">
          <tr>
            <th>Pay Head</th>
            <th class="text-end">Amount</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in payHeadData" :key="index">
            <td>{{ item.pay_head_name }}</td>
            <td class="text-end">{{ item.pay_head_amount }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="col-12 col-md-4 offset-md-1">
        <div class="mb-1">
          <strong>Payroll Advice Setup</strong><br>
          <small>The total percentage of all payment modes must equal <strong>exactly 100%</strong></small>
        </div>

        <table class="table table-bordered table-sm mt-3">
          <thead class="table-light">
          <tr>
            <th>Payment Category</th>
            <th>Value(%)</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in payrollPaymentModeList" :key="index">
            <td>{{ item.name }}</td>
            <td>
              <input type="number"
                     class="form-control"
                     v-model.number="item.percentage"
                     @input="adjustPercentages(index)"
                     min="0"
                     max="100"
              />
            </td>
          </tr>
          </tbody>
        </table>

        <div class="col-sm-6 offset-sm-3">
          <button @click="submit" :disabled="loading" type="submit" class="btn btn-primary me-1 mt-1 btn-sm">Save</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import {defineProps, inject, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import handleHospital from '@/services/modules/hospital'

const emit = defineEmits(['storeContact'])
const props = defineProps({
  items: {
    type: Array
  }
})

const route = useRoute()
const payHeadData = ref([]);
const loading = ref(false);
const salaryTypeData = ref({});
let payrollPaymentModeList = ref([])
const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess')
const {
  getEmployeeSalaryStructure,
  createEmployeePayrollAdviceData,
  fetchEmployeePayrollAdviceData
} = handleHrPayroll()
const { fetchNoteMasterList } = handleHospital()

const getSalaryStructureData = async () => {
  let query = `?company_id=${route.params.companyId}`;
  query += '&contact_profile_id='+ route.query.contactId
  payHeadData.value = [];
  salaryTypeData.value = {};

  await getEmployeeSalaryStructure(query).then(res => {
    if (!res.status){
      payHeadData.value = [];
      salaryTypeData.value = {};
      return
    }
    payHeadData.value = res.data;
    salaryTypeData.value = res.data[0]
  }).catch(err => {
    console.log(err)
  }).finally(() => {
  })
}

const getPaymentMode = async () => {
  let query = '?company_id=' + $route.params.companyId
  query += '&is_all=true&status=active&note_type=payroll_payment_mode'

  payrollPaymentModeList.value = []

  await fetchNoteMasterList(query).then(async res => {
    if (!res.status) {
      return
    }
    payrollPaymentModeList.value = res.data.map((item, index) => ({
      ...item,
      percentage: index === 0 ? 100 : 0
    }))

    await getPayrollAdviceSetupData();
  }).catch(err => {
    console.log(err)
  })
}

const getPayrollAdviceSetupData = async () => {
  let query = '?company_id=' + $route.params.companyId;
  query += '&contact_profile_id=' + route.query.contactId;

  try {
    const res = await fetchEmployeePayrollAdviceData(query);

    if (!res.status) return;

    res.data.forEach(savedItem => {
      const mode = payrollPaymentModeList.value.find(
          item => item.id === savedItem.payment_mode_id
      );

      if (mode) {
        mode.percentage = savedItem.value;
      }
      if (! mode) {
        payrollPaymentModeList.value.push({
          id: savedItem.payment_mode_id,
          name: savedItem.payment_mode_name || 'Unknown',
          percentage: savedItem.value
        });
      }
    });
  } catch (err) {
    console.log('Error fetching payroll advice:', err);
  }
};

const submit = () => {
  loading.value =  true
  let query = `?company_id=${route.params.companyId}`;
  let payrollModeData = [];

  payrollPaymentModeList.value.forEach((item) => {
    payrollModeData.push({
      payment_mode_id: item.id,
      value: item.percentage,
    });
  });

  let payrollAdviceData = {};
  payrollAdviceData.contact_profile_id = route.query.contactId;
  payrollAdviceData.payroll_mode_data = payrollModeData

  createEmployeePayrollAdviceData(payrollAdviceData, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })
}

const adjustPercentages = (changedIndex) => {
  let total = payrollPaymentModeList.value.reduce((sum, item) => sum + item.percentage, 0);

  if (total === 100) return;

  if (total > 100) {
    let excess = total - 100;

    payrollPaymentModeList.value.forEach((item, index) => {
      const canReduce = index !== changedIndex && excess > 0 && item.percentage > 0;
      if (!canReduce) return;

      const reduction = Math.min(item.percentage, excess);
      item.percentage -= reduction;
      excess -= reduction;
    });
  }

  if (total < 100) {
    let deficit = 100 - total;

    payrollPaymentModeList.value.forEach((item, index) => {
      const canIncrease = index !== changedIndex && deficit > 0;
      if (!canIncrease) return;

      item.percentage += deficit;
      deficit = 0;
    });
  }
};

onMounted(() => {
  Promise.all([
    getSalaryStructureData(),
    getPaymentMode()
  ])
})

</script>
